import { useEffect, useMemo, useState } from 'react';
import api from '../api';

function fmtDate(v) {
  if (!v) return '';
  const t = new Date(v);
  if (!Number.isFinite(t.getTime())) return String(v);
  return t.toLocaleString();
}

export default function SocialFeedDebug() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState('');
  const [pollJob, setPollJob] = useState(null);
  const [pollErrors, setPollErrors] = useState([]);

  const [perKindLimit, setPerKindLimit] = useState(10);
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterKind, setFilterKind] = useState('');
  const [filterUser, setFilterUser] = useState('');

  const sorted = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    return list;
  }, [items]);

  const filterOptions = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const basePlatforms = ['twitch', 'youtube', 'tiktok', 'instagram'];
    const platforms = [...new Set([...basePlatforms, ...list.map(i => String(i?.platform || '').trim()).filter(Boolean)])].sort();
    const kinds = [...new Set(list.map(i => String(i?.kind || '').trim()).filter(Boolean))].sort();
    return { platforms, kinds };
  }, [items]);

  const visible = useMemo(() => {
    const list = Array.isArray(sorted) ? sorted : [];
    const p = String(filterPlatform || '').trim().toLowerCase();
    const k = String(filterKind || '').trim().toLowerCase();
    const u = String(filterUser || '').trim().toLowerCase();
    return list.filter(it => {
      if (p && String(it?.platform || '').toLowerCase() !== p) return false;
      if (k && String(it?.kind || '').toLowerCase() !== k) return false;
      if (u) {
        const who = String(it?.user?.displayName || it?.user?.username || '').toLowerCase();
        if (!who.includes(u)) return false;
      }
      return true;
    });
  }, [sorted, filterPlatform, filterKind, filterUser]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/social-feed/debug/items?limit=200');
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      const t = res.data?.total;
      setTotal(Number.isFinite(Number(t)) ? Number(t) : null);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Laden fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  }

  async function runPollNow() {
    setPolling(true);
    setError('');
    setPollResult('');
    setPollJob(null);
    setPollErrors([]);
    try {
      const limit = Number(perKindLimit);
      const per = Number.isFinite(limit) ? Math.min(50, Math.max(1, Math.floor(limit))) : 10;
      const res = await api.post('/social-feed/debug/poll', { perKindLimit: per, includeNoItemWarnings: true });
      const job = res.data?.job || null;
      setPollJob(job);
      setPollResult(`Poll gestartet (Job ${job?.id || '?'}) …`);

      // Poll status for up to ~8 minutes (poll can take a few minutes)
      let done = false;
      for (let i = 0; i < 240; i += 1) {
        await new Promise(r => setTimeout(r, 2000));
        const st = await api.get('/social-feed/debug/poll-status');
        const j = st.data?.job || null;
        if (j) setPollJob(j);

        if (j?.id === job?.id && j.status === 'running') {
          setPollResult(`Poll läuft… (Job ${j.id})`);
        }

        if (j?.id === job?.id && (j.status === 'done' || j.status === 'error')) {
          done = true;
          if (j.status === 'done') {
            const r = j.result || {};
            const errs = Array.isArray(r.errors) ? r.errors : [];
            setPollErrors(errs);
            setPollResult(
              `Poll ok: users ${r.usersChecked || 0} (tw ${r.usersWithTwitch || 0}, yt ${r.usersWithYouTube || 0}, tt ${r.usersWithTikTok || 0}, ig ${r.usersWithInstagram || 0}), created ${r.itemsCreated || 0}, errors ${errs.length}`
            );
            await load();
          } else {
            setError(j?.error?.message || 'Poll fehlgeschlagen.');
          }
          break;
        }
      }

      if (!done) {
        setPollResult('Poll läuft noch im Hintergrund … (bitte später neu laden)');
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        const job = e?.response?.data?.job || null;
        setPollJob(job);
        setPollResult(`Poll läuft bereits (Job ${job?.id || '?'}) …`);
      } else {
      setError(e?.response?.data?.error || e?.message || 'Poll fehlgeschlagen.');
      }
    } finally {
      setPolling(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="home-page">
      <style>{`@keyframes apnspin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Social Feed (Debug)</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign: 'center', maxWidth: 860 }}>
          Zeigt die zuletzt gespeicherten Social-Feed-Items (Twitch/YouTube/TikTok/Instagram) aus dem Backend-Poller.
        </p>
      </section>

      <section style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        {error && <div style={{ color: '#ff8484', marginBottom: 12 }}>{error}</div>}
        {pollResult && <div style={{ color: '#9fe7b2', marginBottom: 12 }}>{pollResult}</div>}
        {pollErrors.length > 0 ? (
          <div style={{ marginBottom: 12, fontSize: 12, opacity: 0.9 }}>
            <div style={{ fontWeight: 800, marginBottom: 6, color: '#ffdf9f' }}>Poll-Fehler (Auszug)</div>
            <div style={{ display:'grid', gap: 6 }}>
              {pollErrors.slice(0, 12).map((er, idx) => (
                <div key={idx} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:'8px 10px' }}>
                  <span style={{ fontWeight: 800 }}>#{er.userId}</span>
                  <span style={{ opacity: 0.8 }}> · {String(er.platform || 'unknown')}</span>
                  <span style={{ opacity: 0.8 }}> · </span>
                  <span style={{ opacity: 0.95 }}>{String(er.error || 'error')}</span>
                  {er.detail ? (
                    <span style={{ opacity: 0.75 }}> · {String(er.detail)}</span>
                  ) : null}
                </div>
              ))}
              {pollErrors.length > 12 ? (
                <div style={{ opacity: 0.7 }}>… {pollErrors.length - 12} weitere</div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div style={{ display:'flex', gap: 10, alignItems:'center', flexWrap:'wrap', marginBottom: 12 }}>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            style={{ background:'#2f2f2f', color:'#ddd', border:'1px solid #555', padding:'8px 12px', borderRadius:10, cursor:'pointer', opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Lade…' : 'Neu laden'}
          </button>

          <label style={{ display:'inline-flex', alignItems:'center', gap: 8, fontSize: 12, opacity: 0.9 }}>
            N pro Art:
            <input
              type="number"
              min={1}
              max={50}
              value={perKindLimit}
              onChange={(e) => setPerKindLimit(e.target.value)}
              style={{ width: 80, padding:'8px 10px', borderRadius: 10, border:'1px solid rgba(255,255,255,0.18)', background:'#111', color:'#fff' }}
              title="Wie viele der neuesten Items pro User/Plattform/Art abgefragt werden"
            />
          </label>

          <button
            type="button"
            onClick={runPollNow}
            disabled={polling}
            style={{ background:'#32CD89', color:'#032d1e', fontWeight:800, border:'1px solid #32CD89', padding:'8px 12px', borderRadius:10, cursor:'pointer', opacity: polling ? 0.7 : 1 }}
            title="Führt den Poller einmal sofort aus und lädt danach neu"
          >
            <span style={{ display:'inline-flex', gap: 8, alignItems:'center' }}>
              {polling ? (
                <span
                  aria-label="Poll läuft"
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 999,
                    border: '2px solid rgba(255,255,255,0.35)',
                    borderTopColor: '#032d1e',
                    animation: 'apnspin 0.8s linear infinite',
                    display: 'inline-block',
                  }}
                />
              ) : null}
              {polling ? 'Poll läuft…' : 'Poll jetzt ausführen'}
            </span>
          </button>
          {pollJob?.status ? (
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              Job: {pollJob.id || '?'} · status: {pollJob.status}
            </div>
          ) : null}
          <div style={{ fontSize: 12, opacity: 0.65 }}>
            Hinweis: Der Cronjob läuft zusätzlich alle ~10 Minuten.
          </div>
        </div>

        <div style={{ display:'flex', gap: 10, alignItems:'center', flexWrap:'wrap', marginBottom: 12 }}>
          <select
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
            style={{ padding:'8px 10px', borderRadius: 10, border:'1px solid rgba(255,255,255,0.18)', background:'#111', color:'#fff' }}
            title="Filter nach Plattform"
          >
            <option value="">Alle Plattformen</option>
            {filterOptions.platforms.map(p => <option key={p} value={p}>{p}</option>)}
          </select>

          <select
            value={filterKind}
            onChange={(e) => setFilterKind(e.target.value)}
            style={{ padding:'8px 10px', borderRadius: 10, border:'1px solid rgba(255,255,255,0.18)', background:'#111', color:'#fff' }}
            title="Filter nach Art/Kind"
          >
            <option value="">Alle Arten</option>
            {filterOptions.kinds.map(k => <option key={k} value={k}>{k}</option>)}
          </select>

          <input
            type="text"
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            placeholder="User-Suche (DisplayName/Username)"
            style={{ minWidth: 260, padding:'8px 10px', borderRadius: 10, border:'1px solid rgba(255,255,255,0.18)', background:'#111', color:'#fff' }}
          />

          <button
            type="button"
            onClick={() => { setFilterPlatform(''); setFilterKind(''); setFilterUser(''); }}
            style={{ background:'#2f2f2f', color:'#ddd', border:'1px solid #555', padding:'8px 12px', borderRadius:10, cursor:'pointer' }}
            title="Setzt alle Filter zurück"
          >
            Filter zurücksetzen
          </button>

          <div style={{ fontSize: 12, opacity: 0.7 }}>
            Anzeige: {visible.length} / {sorted.length}{typeof total === 'number' ? ` (DB: ${total})` : ''}
          </div>
        </div>

        <div style={{ background: '#1e1e1e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 14 }}>
          {loading ? (
            <div style={{ opacity: 0.8 }}>Lade…</div>
          ) : visible.length === 0 ? (
            <div style={{ opacity: 0.7 }}>
              {sorted.length === 0
                ? 'Noch keine Items. (Poll ausführen oder Cronjob abwarten.)'
                : 'Keine Items für die aktuellen Filter.'}
            </div>
          ) : (
            <div style={{ display:'grid', gap: 10 }}>
              {visible.map(it => {
                const u = it?.user;
                const who = (u?.displayName || u?.username || `#${u?.id || ''}`).trim();
                return (
                  <div key={it.id} style={{ border:'1px solid rgba(255,255,255,0.08)', borderRadius:12, padding: 12, background:'#101010' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', gap: 10, flexWrap:'wrap' }}>
                      <div style={{ fontWeight: 900 }}>
                        {String(it.platform || '').toUpperCase()} · {it.kind}
                        <span style={{ fontWeight: 600, opacity: 0.75 }}> · {who}</span>
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        firstSeen: {fmtDate(it.firstSeenAt)}
                      </div>
                    </div>

                    <div style={{ marginTop: 8, fontSize: 14 }}>
                      {it.title ? <div style={{ fontWeight: 800, marginBottom: 6 }}>{it.title}</div> : null}
                      <div style={{ display:'flex', gap: 10, flexWrap:'wrap', alignItems:'center' }}>
                        {it.url ? (
                          <a href={it.url} target="_blank" rel="noreferrer" style={{ color:'#9fbaff', textDecoration:'none' }}>
                            Link öffnen
                          </a>
                        ) : (
                          <span style={{ opacity: 0.6 }}>kein Link</span>
                        )}
                        <span style={{ fontSize: 12, opacity: 0.7 }}>published: {fmtDate(it.publishedAt)}</span>
                        <span style={{ fontSize: 12, opacity: 0.5 }}>externalId: {it.externalId}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
